/**
 * Génère et télécharge un PDF pour un pré-chiffrage donné
 * en utilisant une fenêtre d'impression native (sans dépendance externe).
 */
export function generatePreChiffragePDF(preChiffrage, property) {
  if (!preChiffrage) return

  const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return dateStr }
  }

  const esc = (str) => {
    if (str == null) return ''
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  // En-tête lot
  const address = property?.address
  const addressStr = address
    ? [address.street, address.street2, address.zipCode, address.city].filter(Boolean).join(' ')
    : (property?.fullAddress || '')
  const propertyRef = property?.reference || property?.refLot || property?.id || ''

  const headerHTML = `<div class="pdf-header">
    <div class="pdf-header__brand">PRIMVIEW</div>
    <div class="pdf-header__info">
      ${propertyRef ? `<span class="pdf-header__ref">Réf. lot : ${esc(propertyRef)}</span>` : ''}
      ${addressStr ? `<span>${esc(addressStr)}</span>` : ''}
    </div>
    <div class="pdf-header__date">Édité le ${formatDate(new Date())}</div>
  </div>`

  const type = preChiffrage.isGpr ? 'GPR — EDF' : 'CAT'
  const typeBadgeColor = preChiffrage.isGpr ? '#2563eb' : '#68CFDF'

  const titleHTML = `<div class="pdf-title">
    <h1>Fiche pré-chiffrage</h1>
    <span class="pdf-type-badge" style="background:${typeBadgeColor}">${esc(type)}</span>
  </div>
  <table class="pdf-meta-table">
    <tr>
      <td><strong>Date de création</strong></td><td>${formatDate(preChiffrage.createdAt)}</td>
      <td><strong>Auteur</strong></td><td>${esc(preChiffrage.author) || '—'}</td>
    </tr>
  </table>`

  const badgesHTML = `<div class="pdf-badges">
    ${preChiffrage.chiffrageTermine === 'Oui' ? '<span class="pdf-badge pdf-badge--success">&#10003; Terminé</span>' : ''}
    ${preChiffrage.isSinistre === 'Oui' ? '<span class="pdf-badge pdf-badge--warning">&#9888; Sinistre</span>' : ''}
    ${preChiffrage.photosEnregistrees === 'Oui' ? '<span class="pdf-badge pdf-badge--info">Photos enregistrées</span>' : ''}
    ${preChiffrage.isGpr && preChiffrage.travauxGPRValidesEDF
      ? `<span class="pdf-badge ${preChiffrage.travauxGPRValidesEDF === 'OUI' ? 'pdf-badge--edf-ok' : 'pdf-badge--edf-ko'}">EDF : ${esc(preChiffrage.travauxGPRValidesEDF)}</span>`
      : ''}
  </div>`

  // Récapitulatif GPR
  let gprSummaryHTML = ''
  if (preChiffrage.isGpr) {
    const items = [
      ['Coût total', preChiffrage.coutTotalGPR, '€'],
      ['Durée prévisionnelle', preChiffrage.dureePrevTravauxGPR, 'jours'],
      ['Coût travaux', preChiffrage.coutTravauxGPR, '€'],
      ['Coût DAAT', preChiffrage.coutDAATGPR, '€'],
      ['Coût Diag Élec', preChiffrage.coutDiagElecGPR, '€'],
    ].filter(([, v]) => v != null && v !== '')
    if (items.length) {
      gprSummaryHTML = `<div class="pdf-section">
        <h2 class="pdf-section__title pdf-section__title--gpr"><span class="pdf-badge-label pdf-badge-label--gpr">GPR</span> Récapitulatif GPR (EDF)</h2>
        <table class="pdf-summary-table">
          ${items.map(([l, v, u]) => `<tr><td class="pdf-summary-table__label">${esc(l)}</td><td class="pdf-summary-table__value">${esc(v)} ${u}</td></tr>`).join('')}
        </table>
      </div>`
    }
  }

  // Lignes CAT
  let catLinesHTML = ''
  if (!preChiffrage.isGpr && preChiffrage.lines && preChiffrage.lines.length > 0) {
    const rows = preChiffrage.lines.map((line, idx) => {
      const fields = line.workFields && Object.keys(line.workFields).length
        ? `<div class="pdf-line__fields">${Object.entries(line.workFields).map(([k, v]) =>
            `<span class="pdf-pill"><span class="pdf-pill__key">${esc(k)}</span><span class="pdf-pill__val">${esc(v)}</span></span>`
          ).join('')}</div>`
        : ''
      return `<tr class="pdf-line-row">
        <td class="pdf-line-row__num">${idx + 1}</td>
        <td>
          <div class="pdf-chips">
            ${line.roomType ? `<span class="pdf-chip pdf-chip--room">${esc(line.roomType)}</span>` : ''}
            ${line.workType ? `<span class="pdf-chip pdf-chip--work">${esc(line.workType)}</span>` : ''}
            ${line.sinistre === 'oui' ? `<span class="pdf-chip pdf-chip--sinistre">Sinistre</span>` : ''}
            ${line.montantDiag != null ? `<span class="pdf-chip">${esc(line.montantDiag)} €</span>` : ''}
            ${line.dureeDiag != null ? `<span class="pdf-chip">${esc(line.dureeDiag)} j</span>` : ''}
          </div>
          ${fields}
          ${line.commentairesSinistre ? `<div class="pdf-line__comment">${esc(line.commentairesSinistre)}</div>` : ''}
        </td>
      </tr>`
    }).join('')
    catLinesHTML = `<div class="pdf-section">
      <h2 class="pdf-section__title pdf-section__title--cat"><span class="pdf-badge-label pdf-badge-label--cat">CAT</span> Lignes (${preChiffrage.lines.length})</h2>
      <table class="pdf-lines-table"><tbody>${rows}</tbody></table>
    </div>`
  }

  // Lignes GPR
  let gprLinesHTML = ''
  if (preChiffrage.isGpr && preChiffrage.lines && preChiffrage.lines.length > 0) {
    const rows = preChiffrage.lines.map((line, idx) => `<tr class="pdf-line-row">
      <td class="pdf-line-row__num">${idx + 1}</td>
      <td>
        <div class="pdf-chips">
          ${line.precoTravauxGPR ? `<span class="pdf-chip">${esc(line.precoTravauxGPR)}</span>` : ''}
          ${line.coutTravauxGPR != null ? `<span class="pdf-chip">Trav. : ${esc(line.coutTravauxGPR)} €</span>` : ''}
          ${line.coutDAATGPR != null ? `<span class="pdf-chip">DAAT : ${esc(line.coutDAATGPR)} €</span>` : ''}
          ${line.coutDiagElecGPR != null ? `<span class="pdf-chip">Élec : ${esc(line.coutDiagElecGPR)} €</span>` : ''}
          ${line.dureePrevTravauxGPR != null ? `<span class="pdf-chip">${esc(line.dureePrevTravauxGPR)} j</span>` : ''}
          ${line.travauxGPRValidesEDF
            ? `<span class="pdf-chip ${line.travauxGPRValidesEDF === 'OUI' ? 'pdf-chip--edf-ok' : 'pdf-chip--edf-ko'}">EDF : ${esc(line.travauxGPRValidesEDF)}</span>`
            : ''}
        </div>
        ${line.commentairesSinistre ? `<div class="pdf-line__comment">${esc(line.commentairesSinistre)}</div>` : ''}
      </td>
    </tr>`).join('')
    gprLinesHTML = `<div class="pdf-section">
      <h2 class="pdf-section__title pdf-section__title--gpr"><span class="pdf-badge-label pdf-badge-label--gpr">GPR</span> Lignes GPR (${preChiffrage.lines.length})</h2>
      <table class="pdf-lines-table"><tbody>${rows}</tbody></table>
    </div>`
  }

  // Commentaires généraux
  const commentHTML = preChiffrage.commentairesGeneraux ? `<div class="pdf-section">
    <h2 class="pdf-section__title">Informations complémentaires</h2>
    <div class="pdf-comment-block"><strong>Commentaires généraux :</strong><br>${esc(preChiffrage.commentairesGeneraux)}</div>
  </div>` : ''

  const css = buildPdfCss()
  const fullHTML = `<!DOCTYPE html>
<html lang="fr"><head><meta charset="UTF-8"><title>Pré-chiffrage ${esc(type)}</title><style>${css}</style></head>
<body>${headerHTML}${titleHTML}${badgesHTML}${gprSummaryHTML}${catLinesHTML}${gprLinesHTML}${commentHTML}</body></html>`

  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px'
  document.body.appendChild(iframe)
  const iframeDoc = iframe.contentDocument || iframe.contentWindow.document
  iframeDoc.open(); iframeDoc.write(fullHTML); iframeDoc.close()
  iframe.contentWindow.focus()
  setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 2000) }, 500)
}

function buildPdfCss() {
  return `
    @page{size:A4;margin:18mm 16mm}
    *{box-sizing:border-box;font-family:'Segoe UI',Arial,sans-serif}
    body{font-size:11px;color:#1e293b;background:#fff;margin:0;padding:0}
    .pdf-header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #2563eb;padding-bottom:8px;margin-bottom:14px}
    .pdf-header__brand{font-size:22px;font-weight:900;color:#2563eb;letter-spacing:2px}
    .pdf-header__info{display:flex;flex-direction:column;gap:2px;text-align:center;font-size:10px;color:#475569}
    .pdf-header__ref{font-weight:700;color:#1e293b}
    .pdf-header__date{font-size:10px;color:#94a3b8;text-align:right}
    .pdf-title{display:flex;align-items:center;gap:10px;margin-bottom:8px}
    .pdf-title h1{font-size:16px;font-weight:800;margin:0}
    .pdf-type-badge{padding:2px 10px;border-radius:999px;color:#fff;font-size:10px;font-weight:700}
    .pdf-meta-table{width:100%;border-collapse:collapse;margin-bottom:10px;font-size:10px}
    .pdf-meta-table td{padding:3px 6px}
    .pdf-meta-table td:nth-child(odd){color:#64748b;width:110px}
    .pdf-badges{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:14px}
    .pdf-badge{padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600}
    .pdf-badge--success{background:#dcfce7;color:#166534}
    .pdf-badge--warning{background:#fef9c3;color:#854d0e}
    .pdf-badge--info{background:#dbeafe;color:#1e40af}
    .pdf-badge--edf-ok{background:#d1fae5;color:#065f46}
    .pdf-badge--edf-ko{background:#fee2e2;color:#991b1b}
    .pdf-section{border:1px solid #e2e8f0;border-radius:6px;padding:10px 12px;margin-bottom:12px;page-break-inside:avoid}
    .pdf-section__title{font-size:12px;font-weight:700;margin:0 0 8px;display:flex;align-items:center;gap:6px}
    .pdf-section__title--cat{border-left:3px solid #68CFDF;padding-left:6px}
    .pdf-section__title--gpr{border-left:3px solid #2563eb;padding-left:6px}
    .pdf-badge-label{padding:1px 7px;border-radius:4px;font-size:9px;font-weight:700;color:#fff}
    .pdf-badge-label--cat{background:#68CFDF}.pdf-badge-label--gpr{background:#2563eb}
    .pdf-summary-table{width:100%;border-collapse:collapse}
    .pdf-summary-table tr:nth-child(even) td{background:#f8fafc}
    .pdf-summary-table__label{padding:4px 8px;color:#64748b;width:50%;font-size:10px}
    .pdf-summary-table__value{padding:4px 8px;font-weight:700;color:#2563eb}
    .pdf-lines-table{width:100%;border-collapse:collapse}
    .pdf-line-row td{padding:5px 6px;vertical-align:top;border-bottom:1px solid #f1f5f9}
    .pdf-line-row__num{font-weight:700;width:24px;text-align:center;color:#64748b}
    .pdf-chips{display:flex;flex-wrap:wrap;gap:3px}
    .pdf-chip{padding:1px 6px;border:1px solid #e2e8f0;border-radius:3px;font-size:9px;background:#f8fafc;color:#374151}
    .pdf-chip--room{background:#f0f9ff;border-color:#bae6fd;color:#0369a1}
    .pdf-chip--work{background:#f0fdf4;border-color:#bbf7d0;color:#166534}
    .pdf-chip--sinistre{background:#fef9c3;border-color:#fde047;color:#854d0e}
    .pdf-chip--edf-ok{background:#d1fae5;border-color:#6ee7b7;color:#065f46}
    .pdf-chip--edf-ko{background:#fee2e2;border-color:#fca5a5;color:#991b1b}
    .pdf-line__fields{display:flex;flex-wrap:wrap;gap:3px;margin-top:3px}
    .pdf-pill{display:inline-flex;font-size:9px;border-radius:3px;border:1px solid #e2e8f0;overflow:hidden}
    .pdf-pill__key{background:#f1f5f9;color:#64748b;padding:1px 4px}
    .pdf-pill__val{background:#fff;color:#1e293b;padding:1px 4px;font-weight:600}
    .pdf-line__comment{margin-top:4px;font-size:9px;color:#64748b;font-style:italic}
    .pdf-comment-block{background:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;padding:8px;font-size:10px;line-height:1.5}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  `
}

export default generatePreChiffragePDF


